import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { AttachedFile, FileOwnerRef } from 'src/app/entities';
import {
  bytesToBinaryString,
  extractFileNameFromKey,
  getFileNameWithoutExtension,
} from 'src/app/shared/lib/helpers';

import {
  BinaryFileResponseDto,
  FileStorageAdapter,
  StorageFileItemDto,
  UploadFileResponseDto,
} from '../models/multimedia-file-api.models';

@Injectable({ providedIn: 'root' })
export class FileStorageService {
  private readonly http = inject(HttpClient);

  /**
   * @description Lista los archivos remotos asociados a un propietario específico utilizando un adaptador de almacenamiento de archivos proporcionado, realizando las solicitudes HTTP necesarias para obtener los archivos y construir las instancias de `AttachedFile` correspondientes, manejando errores en caso de que no se puedan cargar los archivos.
   * @param owner 
   * @param adapter 
   * @returns Un observable que emite un array de `AttachedFile` representando los archivos remotos asociados al propietario, con su estado actualizado según el resultado de las solicitudes HTTP para obtener los archivos.
   */
  listRemoteFiles(
    owner: FileOwnerRef,
    adapter: FileStorageAdapter
  ): Observable<AttachedFile[]> {
    return this.http.get<StorageFileItemDto[]>(adapter.listFilesUrl(owner)).pipe(
      switchMap((remoteFiles) => {
        if (!remoteFiles?.length) {
          return of([] as AttachedFile[]);
        }

        const requests = remoteFiles.map((remoteFile) =>
          this.http
            .get<BinaryFileResponseDto>(adapter.getFileUrl(owner, remoteFile))
            .pipe(
              map((response) =>
                this.buildRemoteFile(owner, adapter, remoteFile, response)
              ),
              catchError(() =>
                of(this.buildRemoteErrorFile(owner, adapter, remoteFile))
              )
            )
        );

        return forkJoin(requests);
      }),
      catchError(() => of([] as AttachedFile[]))
    );
  }

  /**
   * @description Sube los archivos pendientes (aquellos que tienen una fuente pero no una clave remota) asociados a un propietario específico utilizando un adaptador de almacenamiento de archivos proporcionado, realizando las solicitudes HTTP necesarias para cargar los archivos y actualizar su estado según el resultado de las operaciones de carga, manejando errores en caso de que no se puedan subir los archivos.
   * @param owner 
   * @param files 
   * @param adapter 
   * @returns Un observable que emite un array de `AttachedFile` con los archivos actualizados después de intentar subir los archivos pendientes, reflejando el estado de cada archivo según el resultado de las operaciones de carga.
   */
  uploadPendingFiles(
    owner: FileOwnerRef,
    files: AttachedFile[],
    adapter: FileStorageAdapter
  ): Observable<AttachedFile[]> {
    const pendingEntries = files
      .map((file, index) => ({ file, index }))
      .filter(({ file }) => !!file.src && !file.key);

    if (!pendingEntries.length) {
      return of(files);
    }

    const requests = pendingEntries.map(({ file, index }) => {
      const payload = adapter.buildUploadPayload?.({ owner, file }) ?? {
        data: file.src,
        entityId: owner.entityId,
        fileName: file.name,
        uploadedBy: file.uploadedBy,
      };

      return this.http.post<UploadFileResponseDto>(adapter.uploadUrl(owner), payload).pipe(
        map((response) => ({
          index,
          file: {
            ...file,
            key: response.key ?? response.id ?? file.key,
            remoteId: response.id ?? response.key ?? file.remoteId,
            status: 'uploaded' as const,
            error: null,
            owner,
          },
        })),
        catchError(() =>
          of({
            index,
            file: {
              ...file,
              status: 'error' as const,
              error: 'No se pudo subir el archivo.',
              owner,
            },
          })
        )
      );
    });

    return forkJoin(requests).pipe(
      map((results) => {
        const nextFiles = [...files];
        results.forEach(({ index, file }) => {
          nextFiles[index] = file;
        });
        return nextFiles;
      })
    );
  }

  /**
   * @description Elimina un archivo remoto específico asociado a un propietario utilizando un adaptador de almacenamiento de archivos proporcionado, realizando la solicitud HTTP necesaria para eliminar el archivo y manejando el resultado de la operación, devolviendo un observable que emite `void` al completar la eliminación o en caso de que no se pueda eliminar el archivo.
   * @param owner 
   * @param file 
   * @param adapter 
   * @returns Un observable que emite `void` al completar la eliminación del archivo remoto o en caso de que no se pueda eliminar el archivo, dependiendo de si el adaptador proporciona una URL para eliminar el archivo y del resultado de la solicitud HTTP de eliminación.
   */
  deleteRemoteFile(
    owner: FileOwnerRef,
    file: AttachedFile,
    adapter: FileStorageAdapter
  ): Observable<void> {
    const url = adapter.deleteFileUrl?.(owner, file);

    if (!url) {
      return of(void 0);
    }

    return this.http.delete<void>(url);
  }

  /**
   * @description Construye una instancia de `AttachedFile` a partir de un archivo remoto representado por `StorageFileItemDto` y la respuesta binaria obtenida al intentar descargar el archivo, utilizando el adaptador de almacenamiento de archivos proporcionado para resolver las propiedades clave, ID y nombre del archivo remoto, y estableciendo el estado del archivo como 'loaded' si se pudo obtener correctamente o 'error' si no se pudo cargar el archivo.
   * @param owner 
   * @param adapter 
   * @param remoteFile 
   * @param response 
   * @returns Una instancia de `AttachedFile` construida a partir del archivo remoto y la respuesta de descarga, con sus propiedades clave, ID, nombre, fuente y estado establecidos según el resultado de la operación de descarga y la lógica definida en el adaptador de almacenamiento de archivos.
   */
  private buildRemoteFile(
    owner: FileOwnerRef,
    adapter: FileStorageAdapter,
    remoteFile: StorageFileItemDto,
    response: BinaryFileResponseDto
  ): AttachedFile {
    const key =
      adapter.resolveRemoteKey?.(remoteFile) ?? remoteFile.Key ?? remoteFile.id;
    const fileName =
      adapter.resolveRemoteName?.(remoteFile) ??
      extractFileNameFromKey(key ?? remoteFile.name ?? 'archivo');

    return {
      key,
      remoteId: adapter.resolveRemoteId?.(remoteFile) ?? remoteFile.id ?? key,
      name: fileName,
      downloadName: getFileNameWithoutExtension(fileName),
      src: bytesToBinaryString(new Uint8Array(response.data)),
      status: 'loaded',
      owner,
    };
  }

  /**
   * @description Construye una instancia de `AttachedFile` para representar un archivo remoto que no se pudo cargar correctamente, utilizando el adaptador de almacenamiento de archivos proporcionado para resolver las propiedades clave, ID y nombre del archivo remoto, y estableciendo el estado del archivo como 'error'.
   * @param owner 
   * @param adapter 
   * @param remoteFile 
   * @returns Una instancia de `AttachedFile` construida con las propiedades del archivo remoto y un estado de error.
   */
  private buildRemoteErrorFile(
    owner: FileOwnerRef,
    adapter: FileStorageAdapter,
    remoteFile: StorageFileItemDto
  ): AttachedFile {
    const key =
      adapter.resolveRemoteKey?.(remoteFile) ?? remoteFile.Key ?? remoteFile.id;
    const fileName =
      adapter.resolveRemoteName?.(remoteFile) ??
      extractFileNameFromKey(key ?? remoteFile.name ?? 'archivo');

    return {
      key,
      remoteId: adapter.resolveRemoteId?.(remoteFile) ?? remoteFile.id ?? key,
      name: fileName,
      downloadName: getFileNameWithoutExtension(fileName),
      status: 'error',
      error: 'No se pudo cargar el archivo.',
      owner,
    };
  }
}
